/**
 * /frontend/src/features/analytics/components/charts/HeatmapGrid.js
 */

import React, { useMemo, useState } from 'react';
import { CHART_COLORS } from '../../constants/chartColors';
import './HeatmapGrid.css';

const levels = [
  'color-mix(in srgb, var(--trae-text-muted) 10%, transparent)',
  'color-mix(in srgb, var(--trae-accent) 20%, transparent)',
  'color-mix(in srgb, var(--trae-accent) 34%, transparent)',
  'color-mix(in srgb, var(--trae-accent-strong) 48%, transparent)',
  'color-mix(in srgb, var(--trae-accent-cyan) 62%, transparent)',
  'color-mix(in srgb, var(--trae-accent-cyan) 78%, transparent)'
];

/**
 * Heatmap grid (7 rows x 24 columns).
 * Expects rows in the form: [{ dayLabel, cells: [{ hour, joinCount, avgWaitMinutes }] }].
 * @param {{rows:any[]}} props
 * @returns {JSX.Element}
 */
const HeatmapGrid = ({ rows }) => {
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const [hover, setHover] = useState(null);
  const max = useMemo(() => resolveMax(safeRows), [safeRows]);

  if (!safeRows.length) {
    return <div className="analytics-chart-empty-inline">No data available for the selected range.</div>;
  }

  return (
    <div className="analytics-heatmap">
      {hover ? (
        <div className="analytics-heatmap-label" style={{ marginBottom: 8, color: CHART_COLORS.textSoft }}>
          {hover.dayLabel} {String(hover.hour).padStart(2, '0')}:00 | Joins: {hover.joinCount} | Avg wait: {hover.avgWaitMinutes?.toFixed?.(1) ?? hover.avgWaitMinutes ?? 0} min
        </div>
      ) : (
        <div className="analytics-heatmap-label" style={{ marginBottom: 8 }}>
          Hover a cell to see details
        </div>
      )}

      <div className="analytics-heatmap-grid">
        <div className="analytics-heatmap-label" />
        {Array.from({ length: 24 }).map((_, hour) => (
          <div key={`h-${hour}`} className="analytics-heatmap-label" style={{ textAlign: 'center' }}>
            {hour}
          </div>
        ))}

        {safeRows.map((row) => (
          <React.Fragment key={row.dayLabel}>
            <div className="analytics-heatmap-label">{row.dayLabel}</div>
            {Array.from({ length: 24 }).map((_, hour) => {
              const cell = (row.cells || []).find((item) => item.hour === hour) || { joinCount: 0, avgWaitMinutes: 0 };
              const bg = levels[heatLevel(cell.joinCount, max)];
              return (
                <div
                  key={`${row.dayLabel}-${hour}`}
                  className="analytics-heatmap-cell"
                  style={{ background: bg }}
                  onMouseEnter={() => setHover({ ...cell, dayLabel: row.dayLabel, hour })}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const resolveMax = (rows) => {
  const values = (rows || []).flatMap((row) => (row.cells || []).map((cell) => Number(cell.joinCount || 0)));
  return Math.max(0, ...values);
};

const heatLevel = (count, maxCount) => {
  if (!maxCount || maxCount <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, Number(count || 0) / maxCount));
  if (ratio === 0) return 0;
  if (ratio <= 0.2) return 1;
  if (ratio <= 0.4) return 2;
  if (ratio <= 0.6) return 3;
  if (ratio <= 0.8) return 4;
  return 5;
};

export default HeatmapGrid;
